import type { SocialMediaPostsResult } from "@/lib/integrations";
import type { ConnectionProvider, IntegrationProvider, PageType, SyncTrigger } from "@prisma/client";
import type { SocialPublishFailureReason, SocialPublishSource } from "@/lib/social/publish-failure-reason";

/**
 * List of all Segment events that we track.
 * For naming conventions, we take inspiration from posthog:
 * https://posthog.com/docs/product-analytics/best-practices#2-implement-a-naming-convention
 *
 * Caveat: We use camelCase for event names instead of snake_case.
 */

export const SEGMENT_EVENTS = {
  // Social Post Publish Flow
  SOCIAL_POST_PUBLISH_START: "socialPostPublish:start",
  SOCIAL_POST_PUBLISH_FINISH: "socialPostPublish:finish",
  SOCIAL_POST_PUBLISH_FAILURE: "socialPostPublish:failure",
  SOCIAL_POST_PUBLISH_TOTAL_LATENCY: "socialPostPublish:totalLatency",
  SOCIAL_PLATFORM_PUBLISH_SUCCESS: "socialPlatformPublish:success",
  SOCIAL_PLATFORM_PUBLISH_FAILURE: "socialPlatformPublish:failure",
  SOCIAL_PLATFORM_AUTO_DISCONNECTED: "socialPlatformAutoDisconnect",
  // Social Post Content
  SOCIAL_POST_CONTENT_GENERATE: "socialPostContent:generate",
  SOCIAL_POST_CONTENT_REGENERATE: "socialPostContent:regenerate",
  SOCIAL_POST_IMAGE_DESCRIPTION_GENERATE: "smm:image_description:generate",
  SOCIAL_POST_IMAGE_DESCRIPTION_FAILURE: "smm:image_description:failure",
  // Google Business Profile Link Viewed
  GBP_VIEWED: "gbpViewed",
  GBP_REDIRECT_VISITED: "gbpRedirectVisited",
  // Social Media Scrape Started
  SOCIAL_MEDIA_SCRAPE_STARTED: "socialMediaScrapeStarted",
  SOCIAL_MEDIA_SCRAPE_COMPLETED: "socialMediaScrapeCompleted",
  // Widget Toggled
  WIDGET_TOGGLED: "widgetToggled",
  // Widget Option Toggled
  WIDGET_OPTION_TOGGLED: "widgetOptionToggled",
  // Call Sync
  CALL_COMPLETED: "callCompleted",
  // Social Media
  SOCIAL_MEDIA_POST_VIEWED: "smmPostViewed",
  SOCIAL_MEDIA_POST_CREATED: "smmPostCreated",
  // Voice to Quote
  QUOTE_STARTED: "quoteStarted",
  QUOTE_PDF_DOWNLOADED: "quotePdfDownloaded",
  QUOTE_EMAIL_SENT: "quoteEmailSent",
  QUOTE_SMS_SENT: "quoteSmsSent",
  QUOTE_COMPLETED: "quoteCompleted",
  // Review Request
  REVIEW_REQUEST_STARTED: "review:request:start",
  REVIEW_REQUEST_CREATED: "review:request:create",
  REVIEW_REQUEST_EMAIL_SENT: "review:request:email:sent",
  REVIEW_REQUEST_SMS_SENT: "review:request:sms:sent",
  REVIEW_REQUEST_EMAIL_FAILURE: "review:request:email:failure",
  REVIEW_REQUEST_SMS_FAILURE: "review:request:sms:failure",
  // Review Replys
  REVIEW_REPLY_GOOGLE_SUCCESS: "review:reply_google:success",
  REVIEW_REPLY_INTERNAL_SUCCESS: "review:reply_internal:success",
  REVIEW_REPLY_GOOGLE_FAILURE: "review:reply_google:failure",
  REVIEW_REPLY_INTERNAL_FAILURE: "review:reply_internal:failure",
  REVIEW_REPLY_REGENERATE: "review:reply:regenerate",
  REVIEW_REPLY_EDIT: "review:reply:edit",
  // Social Reminder
  SMM_NOTIFICATION_PUSHED: "smmNotificationPushed",
  // QR Code Shared
  GBP_SHARED: "gbpShared",
  // Reconnect Integrations Requested
  RECONNECT_INTEGRATIONS_REQUESTED: "reconnectIntegrationsRequested",
  // Leads Details
  LEADS_DETAIL_CTA_CLICKED: "leadsDetailCtaClicked",
  LEADS_DETAIL_VIEWED: "leadsDetailViewed",
  // Organization Invite
  ORGANIZATION_INVITE: "organization:member:invite",
  // WEBSITE events
  WEBSITE_PAGE_CREATED: "website:page:create",
  WEBSITE_PAGE_DELETED: "website:page:delete",
  WEBSITE_IMAGE_UPLOAD_START: "website:image_upload:start",
  WEBSITE_IMAGE_UPLOAD_TAG: "website:image_upload:tag",
  WEBSITE_IMAGE_UPLOAD_FINISH: "website:image_upload:finish",
  WEBSITE_BLOG_POST_CREATED: "website:blog_post:created",
  WEBSITE_BLOG_POST_DELETED: "website:blog_post:deleted",
  WEBSITE_BLOG_POST_UPDATED: "website:blog_post:updated",

  // Review Portal (client-facing)
  REVIEW_PORTAL_REQUEST_OPENED: "reviewRequestOpened",
  REVIEW_PORTAL_NEGATIVE_FEEDBACK_SUBMITTED: "negativeFeedbackSubmitted",
  REVIEW_PORTAL_RATE_US_NOW_CLICKED: "rateUsNowClicked",

  // Project events
  PROJECT_START: "project:start",
  PROJECT_IMAGE_UPLOAD: "project:image:upload",
  PROJECT_IMAGE_REORDER: "project:image:reorder",
  PROJECT_LOCATION_SET: "project:location:set",
  PROJECT_TEXT_REGENERATE: "project:text:regenerate",
  PROJECT_PUBLISH_START: "project:publish:start",
  PROJECT_PUBLISH_FINISH: "project:publish:finish",

  // Onboarding
  CRM_ONBOARDING_STEP_COMPLETE: "crm:onboarding:step_complete",

  // Dashboard
  CRM_DASHBOARD_VIEW: "crm:dashboard:view",
  CRM_DASHBOARD_CATCHUP: "crm:dashboard:catchup",
  CRM_DASHBOARD_URGENT_ITEM_CLICK: "crm:dashboard:urgent_item_click",
  CRM_DASHBOARD_PIPELINE: "crm:dashboard:pipeline",

  // Catch Up
  CATCHUP_VIEW: "catchup:view",
  CATCHUP_FILTER: "catchup:filter",
  CATCHUP_ITEM_ACTION: "catchup:item:action",
  CATCHUP_ITEM_SKIP: "catchup:item:skip",

  // Quotes
  QUOTE_VIEW_LOAD: "quote:view:load",
  QUOTE_FILTER_APPLY: "quote:filter:apply",
  QUOTE_CREATE_START: "quote:create:start",
  QUOTE_CREATE_STEP_COMPLETE: "quote:create:step_complete",
  QUOTE_DEPOSIT_TOGGLE: "quote:deposit:toggle",
  QUOTE_PAYMENT_METHOD_SELECT: "quote:payment_method:select",
  QUOTE_GROUP_CREATE: "quote:group:create",
  QUOTE_PREVIEW_VIEW: "quote:preview:view",
  QUOTE_PREVIEW_EDIT: "quote:preview:edit",
  QUOTE_SEND_CLICK: "quote:send:click",
  QUOTE_REMINDER_CLICK: "quote:reminder:click",
  QUOTE_STATUS_ACCEPT: "quote:status:accept",
  QUOTE_DEPOSIT_MARK_AS_PAID_CLICK: "quote:deposit_mark_as_paid:click",
  QUOTE_CONVERT_JOB: "quote:convert:job",
  QUOTE_CONVERT_INVOICE: "quote:convert:invoice",

  // Scheduling / Appointments
  CALENDAR_VIEW_LOAD: "calendar:view:load",
  APPOINTMENT_CREATE_START: "appointment:create:start",
  APPOINTMENT_CREATE_SUBMIT: "appointment:create:submit",
  APPOINTMENT_EDIT_SAVE: "appointment:edit:save",
  APPOINTMENT_CANCEL_CLICK: "appointment:cancel:click",
  APPOINTMENT_RESCHEDULE_CLICK: "appointment:reschedule:click",

  // Jobs
  JOB_VIEW_LOAD: "job:view:load",
  JOB_CREATE_START: "job:create:start",
  JOB_CREATE_STEP_COMPLETE: "job:create:step_complete",
  JOB_CREATE_SUBMIT: "job:create:submit",
  JOB_LINK_QUOTE: "job:link:quote",
  JOB_LINK_INVOICE: "job:link:invoice",
  JOB_LINK_APPOINTMENT: "job:link:appointment",
  JOB_RECURRING_ENABLE: "job:recurring:enable", //TODO: Enable once implemented

  // Invoices
  INVOICE_VIEW_LOAD: "invoice:view:load",
  INVOICE_FILTER_APPLY: "invoice:filter:apply",
  INVOICE_CREATE_START: "invoice:create:start",
  INVOICE_CREATE_STEP_COMPLETE: "invoice:create:step_complete",
  INVOICE_CREATE_SUBMIT: "invoice:create:submit",
  INVOICE_PAYMENT_METHOD_SELECT: "invoice:payment_method:select",
  INVOICE_PREVIEW_VIEW: "invoice:preview:view",
  INVOICE_PREVIEW_EDIT: "invoice:preview:edit",
  INVOICE_EDIT_SAVE: "invoice:edit:save",
  INVOICE_SEND_CLICK: "invoice:send:click",
  INVOICE_REMINDER_CLICK: "invoice:reminder:click",
  INVOICE_MARK_AS_PAID_CLICK: "invoice:mark_as_paid:click",
  INVOICE_PAYMENT_COMPLETE: "invoice:payment:complete",

  // Payments
  PAYMENT_ONBOARDING_START: "payment:onboarding:start",
  PAYMENT_ONBOARDING_INFORMATION_REQUIRED: "payment:onboarding:information_required",
  PAYMENT_REPORT_VIEW: "payment:report:view",
  PAYMENT_REPORT_DEPOSIT_VIEW: "payment:report_deposit:view",
  PAYMENT_REPORT_CHARGEBACK_VIEW: "payment:report_chargeback:view",

  // Client Portal
  PORTAL_VIEW_LOAD: "portal:view:load",
  PORTAL_QUOTE_VIEW: "portal:quote:view",
  PORTAL_INVOICE_VIEW: "portal:invoice:view",
  PORTAL_JOB_VIEW: "portal:job:view",
  PORTAL_QUOTE_APPROVE: "portal:quote:approve",
  PORTAL_QUOTE_REJECT: "portal:quote:reject",
  PORTAL_QUOTE_REVISION: "portal:quote:revision",
  PORTAL_QUOTE_DEPOSIT_START: "portal:quote_deposit:start",
  PORTAL_QUOTE_DEPOSIT_SUBMIT: "portal:quote_deposit:submit",
  PORTAL_QUOTE_DEPOSIT_ERROR: "portal:quote_deposit:error",
  PORTAL_PAYMENT_START: "portal:payment:start",
  PORTAL_PAYMENT_SUBMIT: "portal:payment:submit",
  PORTAL_PAYMENT_ERROR: "portal:payment:error",
  PORTAL_REFER_FRIEND_CLICK: "portal:refer_friend:click",
  PORTAL_REVIEW_SUBMIT: "portal:review:submit",

  // Team Management
  ACCOUNT_USER_INVITE: "account:user:invite",
  ACCOUNT_USER_ACCEPT_INVITE: "account:user:accept_invite",
  ACCOUNT_USER_DELETE: "account:user:delete",

  // QuickBooks
  QBO_CONNECTED: "qbo:connected",
  QBO_RECONNECTED: "qbo:reconnected",
  QBO_DISCONNECTED: "qbo:disconnected",

  // Integrations (calendar connections — Google Calendar, ICS feeds)
  INTEGRATION_CONNECTED: "integration:connected",
  INTEGRATION_DISCONNECTED: "integration:disconnected",
  INTEGRATION_SYNCED: "integration:synced",
  INTEGRATION_ERRORED: "integration:errored",
  INTEGRATION_CALENDAR_ENABLED: "integration:calendar_enabled",
  INTEGRATION_CALENDAR_DISABLED: "integration:calendar_disabled",

  // Time Tracking — admin Timesheets dashboard
  CRM_TIMESHEETS_ADMIN_VIEW: "crm:timesheets_admin:view",
  CRM_TIMESHEETS_ADMIN_CSV_EXPORT: "crm:timesheets_admin:csv_export",
  CRM_TIMESHEETS_ADMIN_ENTRY_EDIT: "crm:timesheets_admin:entry_edit",
  CRM_TIMESHEETS_ADMIN_ENTRY_REVIEW: "crm:timesheets_admin:entry_review",

  // Time Tracking — crew clock-in/out + appointment timer
  TIME_TRACKING_CLOCK_IN: "time_tracking:clock_in",
  TIME_TRACKING_CLOCK_OUT: "time_tracking:clock_out",
  TIME_TRACKING_APPOINTMENT_START: "time_tracking:appointment:start",
  TIME_TRACKING_APPOINTMENT_STOP: "time_tracking:appointment:stop",

  // Auth health
  AUTH_RECOVERY_SUCCEEDED: "auth:recovery:succeeded",
  AUTH_RECOVERY_FAILED: "auth:recovery:failed",

  // Rainforest Application
  RAINFOREST_APPLICATION_PENDING: "rainforest:application:pending",
  RAINFOREST_APPLICATION_IN_PROGRESS: "rainforest:application:in_progress",
  RAINFOREST_APPLICATION_PROCESSING: "rainforest:application:processing",
  RAINFOREST_APPLICATION_NEEDS_INFORMATION: "rainforest:application:needs_information",
  RAINFOREST_APPLICATION_IN_REVIEW: "rainforest:application:in_review",
  RAINFOREST_APPLICATION_COMPLETED: "rainforest:application:completed",
  RAINFOREST_APPLICATION_DECLINED: "rainforest:application:declined",
  RAINFOREST_APPLICATION_EXPIRED: "rainforest:application:expired",
} as const;

export type SegmentEvent = (typeof SEGMENT_EVENTS)[keyof typeof SEGMENT_EVENTS];

// Helper functions to unify the event names with the expected properties

export type SegmentEventSchema = {
  [SEGMENT_EVENTS.SOCIAL_POST_PUBLISH_START]: {
    websiteId: string;
    postId: string;
  };
  [SEGMENT_EVENTS.SOCIAL_POST_PUBLISH_FINISH]: {
    websiteId: string;
    postId: string;
    socialMediaPostsResult?: SocialMediaPostsResult;
    totalLatencyMs?: number;
  };
  [SEGMENT_EVENTS.SOCIAL_POST_PUBLISH_FAILURE]: {
    websiteId: string;
    postId: string;
    error: string;
  };
  [SEGMENT_EVENTS.SOCIAL_PLATFORM_PUBLISH_SUCCESS]: {
    websiteId: string;
    postId: string;
    provider: IntegrationProvider;
    publishSource?: SocialPublishSource;
    /** Present when Instagram publish dropped items beyond the carousel cap. */
    trimmedItemCount?: number;
  };
  [SEGMENT_EVENTS.SOCIAL_PLATFORM_PUBLISH_FAILURE]: {
    websiteId: string;
    postId: string;
    provider: IntegrationProvider;
    failureReason: SocialPublishFailureReason;
    publishSource: SocialPublishSource;
    error?: string;
    userErrorMessage?: string;
    errorType?: string;
    errorCode?: number;
    errorSubcode?: number;
    cancelLogToSentry?: boolean;
    /** Present when Instagram failed while processing a specific media container. */
    failedMediaUrl?: string;
  };
  [SEGMENT_EVENTS.SOCIAL_PLATFORM_AUTO_DISCONNECTED]: {
    websiteId: string;
    postId: string;
    provider: IntegrationProvider;
  };
  [SEGMENT_EVENTS.SOCIAL_POST_CONTENT_GENERATE]: {
    websiteId: string;
    postId: string;
  };
  [SEGMENT_EVENTS.SOCIAL_POST_CONTENT_REGENERATE]: {
    websiteId: string;
    postId: string;
  };
  [SEGMENT_EVENTS.WEBSITE_PAGE_CREATED]: WebsitePageEventProps;
  [SEGMENT_EVENTS.WEBSITE_PAGE_DELETED]: WebsitePageEventProps;
  [SEGMENT_EVENTS.ORGANIZATION_INVITE]: {
    organizationId: string;
    emailAddress: string;
    role: string;
  };
  [SEGMENT_EVENTS.WEBSITE_IMAGE_UPLOAD_START]: {
    websiteId: string;
  };
  [SEGMENT_EVENTS.WEBSITE_IMAGE_UPLOAD_TAG]: {
    websiteId: string;
    imageIdToTag: string;
    serviceIdsToAdd: string[];
    serviceIdsToRemove: string[];
  };
  [SEGMENT_EVENTS.WEBSITE_IMAGE_UPLOAD_FINISH]: {
    websiteId: string;
    imageCount: number;
  };
  [SEGMENT_EVENTS.WEBSITE_BLOG_POST_CREATED]: WebsiteBlogPostEventProps;
  [SEGMENT_EVENTS.WEBSITE_BLOG_POST_DELETED]: WebsiteBlogPostEventProps;
  [SEGMENT_EVENTS.WEBSITE_BLOG_POST_UPDATED]: WebsiteBlogPostEventProps;
  [SEGMENT_EVENTS.REVIEW_REQUEST_STARTED]: {
    websiteId: string;
    customerRecordId: string;
    reviewRequestId: string;
    requestLocation: string;
    jobId: string;
    useStarRating: boolean;
  };
  [SEGMENT_EVENTS.REVIEW_REQUEST_EMAIL_SENT]: ReviewRequestNotificationEventProps;
  [SEGMENT_EVENTS.REVIEW_REQUEST_SMS_SENT]: ReviewRequestNotificationEventProps;
  [SEGMENT_EVENTS.REVIEW_REQUEST_EMAIL_FAILURE]: ReviewRequestNotificationEventProps & {
    error: string;
  };
  [SEGMENT_EVENTS.REVIEW_REQUEST_SMS_FAILURE]: ReviewRequestNotificationEventProps & {
    error: string;
  };
  [SEGMENT_EVENTS.REVIEW_REQUEST_CREATED]: {
    websiteId: string;
    customerRecordId: string;
  };
  [SEGMENT_EVENTS.REVIEW_PORTAL_REQUEST_OPENED]: {
    websiteId: string;
    reviewRequestId: string;
  };
  [SEGMENT_EVENTS.REVIEW_PORTAL_NEGATIVE_FEEDBACK_SUBMITTED]: {
    websiteId: string;
    jobId?: string;
    rating: number;
    prospectName: string;
    reviewRequestId: string;
  };
  [SEGMENT_EVENTS.REVIEW_PORTAL_RATE_US_NOW_CLICKED]: {
    rating: number;
    websiteId: string;
    googleReviewUrl: string;
  };
  [SEGMENT_EVENTS.PROJECT_START]: ProjectEventProps;
  [SEGMENT_EVENTS.PROJECT_IMAGE_UPLOAD]: ProjectEventProps & {
    imageCount: number;
    update: boolean;
  };
  [SEGMENT_EVENTS.PROJECT_IMAGE_REORDER]: ProjectEventProps;
  [SEGMENT_EVENTS.PROJECT_LOCATION_SET]: ProjectEventProps & {
    projectLocation: string;
    source: "EXIF" | "manual";
    update: boolean;
  };
  [SEGMENT_EVENTS.PROJECT_TEXT_REGENERATE]: ProjectEventProps;
  [SEGMENT_EVENTS.PROJECT_PUBLISH_START]: ProjectEventProps & {
    availablePlatforms: IntegrationProvider[];
  };
  [SEGMENT_EVENTS.PROJECT_PUBLISH_FINISH]: ProjectEventProps & {
    publishedPlatforms: IntegrationProvider[];
    projectId?: string;
  };
  [SEGMENT_EVENTS.REVIEW_REPLY_GOOGLE_SUCCESS]: ReviewReplyEventProps;
  [SEGMENT_EVENTS.REVIEW_REPLY_INTERNAL_SUCCESS]: ReviewReplyEventProps;
  [SEGMENT_EVENTS.REVIEW_REPLY_GOOGLE_FAILURE]: ReviewReplyEventProps & {
    error: string;
  };
  [SEGMENT_EVENTS.REVIEW_REPLY_INTERNAL_FAILURE]: ReviewReplyEventProps & {
    error: string;
  };
  [SEGMENT_EVENTS.REVIEW_REPLY_REGENERATE]: ReviewReplyEventProps & {
    originalReply: string;
  };
  [SEGMENT_EVENTS.REVIEW_REPLY_EDIT]: ReviewReplyEventProps & {
    originalReply: string;
  };
  [SEGMENT_EVENTS.SOCIAL_POST_IMAGE_DESCRIPTION_GENERATE]: {
    websiteId: string;
    postId: string;
    imageCount: number;
    totalLatencyMs: number;
  };
  [SEGMENT_EVENTS.SOCIAL_POST_IMAGE_DESCRIPTION_FAILURE]: {
    websiteId: string;
    postId: string;
    imageCount: number;
    error?: string;
  };

  // Onboarding
  [SEGMENT_EVENTS.CRM_ONBOARDING_STEP_COMPLETE]: {
    step_name: string;
    stepIndex: number;
    formData: Record<string, unknown>;
  };

  // Dashboard
  [SEGMENT_EVENTS.CRM_DASHBOARD_VIEW]: { websiteId: string };
  [SEGMENT_EVENTS.CRM_DASHBOARD_CATCHUP]: { websiteId: string };
  [SEGMENT_EVENTS.CRM_DASHBOARD_URGENT_ITEM_CLICK]: {
    websiteId: string;
    asset_type: CrmAssetType;
  };
  [SEGMENT_EVENTS.CRM_DASHBOARD_PIPELINE]: { websiteId: string };

  // Catch Up
  [SEGMENT_EVENTS.CATCHUP_VIEW]: { websiteId: string };
  [SEGMENT_EVENTS.CATCHUP_FILTER]: {
    websiteId: string;
    filter: string;
  };
  [SEGMENT_EVENTS.CATCHUP_ITEM_ACTION]: {
    websiteId: string;
    asset_type: CrmAssetType;
    action_type: string;
  };
  [SEGMENT_EVENTS.CATCHUP_ITEM_SKIP]: {
    websiteId: string;
    asset_type: CrmAssetType;
  };

  // Quotes
  [SEGMENT_EVENTS.QUOTE_VIEW_LOAD]: { websiteId: string };
  [SEGMENT_EVENTS.QUOTE_FILTER_APPLY]: FilterApplyEventProps;
  [SEGMENT_EVENTS.QUOTE_CREATE_START]: CreateFlowEventProps;
  [SEGMENT_EVENTS.QUOTE_CREATE_STEP_COMPLETE]: CreateStepCompleteEventProps;
  [SEGMENT_EVENTS.QUOTE_DEPOSIT_TOGGLE]: DepositToggleEventProps;
  [SEGMENT_EVENTS.QUOTE_PAYMENT_METHOD_SELECT]: PaymentMethodSelectEventProps;
  [SEGMENT_EVENTS.QUOTE_GROUP_CREATE]: { websiteId: string };
  [SEGMENT_EVENTS.QUOTE_PREVIEW_VIEW]: { websiteId: string };
  [SEGMENT_EVENTS.QUOTE_PREVIEW_EDIT]: { websiteId: string };
  [SEGMENT_EVENTS.QUOTE_SEND_CLICK]: { websiteId: string };
  [SEGMENT_EVENTS.QUOTE_REMINDER_CLICK]: { websiteId: string };
  [SEGMENT_EVENTS.QUOTE_STATUS_ACCEPT]: { websiteId: string };
  [SEGMENT_EVENTS.QUOTE_DEPOSIT_MARK_AS_PAID_CLICK]: { websiteId: string };
  [SEGMENT_EVENTS.QUOTE_CONVERT_JOB]: { websiteId: string };
  [SEGMENT_EVENTS.QUOTE_CONVERT_INVOICE]: { websiteId: string };

  // Scheduling / Appointments
  [SEGMENT_EVENTS.CALENDAR_VIEW_LOAD]: { websiteId: string };
  [SEGMENT_EVENTS.APPOINTMENT_CREATE_START]: { websiteId: string };
  [SEGMENT_EVENTS.APPOINTMENT_CREATE_SUBMIT]: { websiteId: string };
  [SEGMENT_EVENTS.APPOINTMENT_EDIT_SAVE]: { websiteId: string };
  [SEGMENT_EVENTS.APPOINTMENT_CANCEL_CLICK]: { websiteId: string };
  [SEGMENT_EVENTS.APPOINTMENT_RESCHEDULE_CLICK]: { websiteId: string };

  // Jobs
  [SEGMENT_EVENTS.JOB_VIEW_LOAD]: { websiteId: string };
  [SEGMENT_EVENTS.JOB_CREATE_START]: CreateFlowEventProps;
  [SEGMENT_EVENTS.JOB_CREATE_STEP_COMPLETE]: CreateStepCompleteEventProps;
  [SEGMENT_EVENTS.JOB_CREATE_SUBMIT]: { websiteId: string };
  [SEGMENT_EVENTS.JOB_LINK_QUOTE]: { websiteId: string };
  [SEGMENT_EVENTS.JOB_LINK_INVOICE]: { websiteId: string };
  [SEGMENT_EVENTS.JOB_LINK_APPOINTMENT]: { websiteId: string };
  [SEGMENT_EVENTS.JOB_RECURRING_ENABLE]: { websiteId: string };

  // Invoices
  [SEGMENT_EVENTS.INVOICE_VIEW_LOAD]: { websiteId: string };
  [SEGMENT_EVENTS.INVOICE_FILTER_APPLY]: FilterApplyEventProps;
  [SEGMENT_EVENTS.INVOICE_CREATE_START]: CreateFlowEventProps;
  [SEGMENT_EVENTS.INVOICE_CREATE_STEP_COMPLETE]: CreateStepCompleteEventProps;
  [SEGMENT_EVENTS.INVOICE_CREATE_SUBMIT]: { websiteId: string };
  [SEGMENT_EVENTS.INVOICE_PAYMENT_METHOD_SELECT]: PaymentMethodSelectEventProps;
  [SEGMENT_EVENTS.INVOICE_PREVIEW_VIEW]: { websiteId: string };
  [SEGMENT_EVENTS.INVOICE_PREVIEW_EDIT]: { websiteId: string };
  [SEGMENT_EVENTS.INVOICE_EDIT_SAVE]: { websiteId: string };
  [SEGMENT_EVENTS.INVOICE_SEND_CLICK]: { websiteId: string };
  [SEGMENT_EVENTS.INVOICE_REMINDER_CLICK]: { websiteId: string };
  [SEGMENT_EVENTS.INVOICE_MARK_AS_PAID_CLICK]: { websiteId: string };
  [SEGMENT_EVENTS.INVOICE_PAYMENT_COMPLETE]: { websiteId: string };

  // Payments
  [SEGMENT_EVENTS.PAYMENT_ONBOARDING_START]: { websiteId: string };
  [SEGMENT_EVENTS.PAYMENT_ONBOARDING_INFORMATION_REQUIRED]: { websiteId: string };
  [SEGMENT_EVENTS.PAYMENT_REPORT_VIEW]: { websiteId: string };
  [SEGMENT_EVENTS.PAYMENT_REPORT_DEPOSIT_VIEW]: { websiteId: string };
  [SEGMENT_EVENTS.PAYMENT_REPORT_CHARGEBACK_VIEW]: { websiteId: string };

  // Client Portal
  [SEGMENT_EVENTS.PORTAL_VIEW_LOAD]: { websiteId: string };
  [SEGMENT_EVENTS.PORTAL_QUOTE_VIEW]: { websiteId: string; quoteId: string };
  [SEGMENT_EVENTS.PORTAL_INVOICE_VIEW]: { websiteId: string; invoiceId: string };
  [SEGMENT_EVENTS.PORTAL_JOB_VIEW]: { websiteId: string; jobId: string };
  [SEGMENT_EVENTS.PORTAL_QUOTE_APPROVE]: { websiteId: string; quoteId: string };
  [SEGMENT_EVENTS.PORTAL_QUOTE_REJECT]: { websiteId: string; quoteId: string };
  [SEGMENT_EVENTS.PORTAL_QUOTE_REVISION]: { websiteId: string; quoteId: string };
  [SEGMENT_EVENTS.PORTAL_QUOTE_DEPOSIT_START]: { websiteId: string; quoteId: string };
  [SEGMENT_EVENTS.PORTAL_QUOTE_DEPOSIT_SUBMIT]: { websiteId: string; quoteId: string };
  [SEGMENT_EVENTS.PORTAL_QUOTE_DEPOSIT_ERROR]: {
    websiteId: string;
    quoteId: string;
    error?: string;
  };
  [SEGMENT_EVENTS.PORTAL_PAYMENT_START]: { websiteId: string; invoiceId: string };
  [SEGMENT_EVENTS.PORTAL_PAYMENT_SUBMIT]: { websiteId: string; invoiceId: string };
  [SEGMENT_EVENTS.PORTAL_PAYMENT_ERROR]: {
    websiteId: string;
    invoiceId: string;
    error?: string;
  };
  [SEGMENT_EVENTS.PORTAL_REFER_FRIEND_CLICK]: { websiteId: string };
  [SEGMENT_EVENTS.PORTAL_REVIEW_SUBMIT]: { websiteId: string };

  // Team Management
  [SEGMENT_EVENTS.ACCOUNT_USER_INVITE]: InviteEventProps;
  [SEGMENT_EVENTS.ACCOUNT_USER_ACCEPT_INVITE]: {
    organizationId: string;
    role_assigned: string;
  };
  [SEGMENT_EVENTS.ACCOUNT_USER_DELETE]: {
    organizationId: string;
    role_removed: string;
  };

  // Time Tracking — admin Timesheets dashboard
  [SEGMENT_EVENTS.CRM_TIMESHEETS_ADMIN_VIEW]: { websiteId: string };
  [SEGMENT_EVENTS.CRM_TIMESHEETS_ADMIN_CSV_EXPORT]: TimesheetsAdminCsvExportEventProps;
  [SEGMENT_EVENTS.CRM_TIMESHEETS_ADMIN_ENTRY_EDIT]: TimesheetsAdminEntryEditEventProps;
  [SEGMENT_EVENTS.CRM_TIMESHEETS_ADMIN_ENTRY_REVIEW]: TimesheetsAdminEntryReviewEventProps;

  // Time Tracking — crew clock-in/out + appointment timer
  [SEGMENT_EVENTS.TIME_TRACKING_CLOCK_IN]: TimeTrackingClockEventProps;
  [SEGMENT_EVENTS.TIME_TRACKING_CLOCK_OUT]: TimeTrackingClockOutEventProps;
  [SEGMENT_EVENTS.TIME_TRACKING_APPOINTMENT_START]: TimeTrackingAppointmentEventProps;
  [SEGMENT_EVENTS.TIME_TRACKING_APPOINTMENT_STOP]: TimeTrackingAppointmentEventProps;

  // QuickBooks
  [SEGMENT_EVENTS.QBO_CONNECTED]: QboLifecycleEventProps;
  [SEGMENT_EVENTS.QBO_RECONNECTED]: QboLifecycleEventProps;
  [SEGMENT_EVENTS.QBO_DISCONNECTED]: QboDisconnectEventProps;

  // Auth health
  [SEGMENT_EVENTS.AUTH_RECOVERY_SUCCEEDED]: {
    swrKey: string;
    msToRecovery: number;
  };
  [SEGMENT_EVENTS.AUTH_RECOVERY_FAILED]: {
    swrKey: string;
    statusCode: 401;
    msToRecovery: number;
    hadActiveClerkSession: boolean;
    clerkSessionId: string | null;
  };

  // Integrations (calendar connections)
  [SEGMENT_EVENTS.INTEGRATION_CONNECTED]: IntegrationEventProps;
  [SEGMENT_EVENTS.INTEGRATION_DISCONNECTED]: IntegrationEventProps;
  [SEGMENT_EVENTS.INTEGRATION_SYNCED]: IntegrationSyncedEventProps;
  [SEGMENT_EVENTS.INTEGRATION_ERRORED]: IntegrationErroredEventProps;
  [SEGMENT_EVENTS.INTEGRATION_CALENDAR_ENABLED]: IntegrationCalendarToggleEventProps;
  [SEGMENT_EVENTS.INTEGRATION_CALENDAR_DISABLED]: IntegrationCalendarToggleEventProps;

  // Rainforest Application
  [SEGMENT_EVENTS.RAINFOREST_APPLICATION_PENDING]: RainforestApplicationEventProps;
  [SEGMENT_EVENTS.RAINFOREST_APPLICATION_IN_PROGRESS]: RainforestApplicationEventProps;
  [SEGMENT_EVENTS.RAINFOREST_APPLICATION_PROCESSING]: RainforestApplicationEventProps;
  [SEGMENT_EVENTS.RAINFOREST_APPLICATION_NEEDS_INFORMATION]: RainforestApplicationEventProps;
  [SEGMENT_EVENTS.RAINFOREST_APPLICATION_IN_REVIEW]: RainforestApplicationEventProps;
  [SEGMENT_EVENTS.RAINFOREST_APPLICATION_COMPLETED]: RainforestApplicationEventProps;
  [SEGMENT_EVENTS.RAINFOREST_APPLICATION_DECLINED]: RainforestApplicationEventProps;
  [SEGMENT_EVENTS.RAINFOREST_APPLICATION_EXPIRED]: RainforestApplicationEventProps;
};

export type SegmentEventProperties = {
  [E in keyof SegmentEventSchema]: {
    event: E;
    userId: string | null;
    properties: SegmentEventSchema[E];
  };
}[keyof SegmentEventSchema];

// Type for events that have not yet been defined in SegmentEventSchema.
// used in clientSideTrack and serverSideTrack, once we overhaul all events
// we can replace this with SegmentEventProperties
export type GenericSegmentEventProperties = {
  event: SegmentEvent | (string & {});
  userId?: string | null;
  properties: Record<string, unknown>;
};

export function createSegmentEvent(props: SegmentEventProperties): SegmentEventProperties {
  return props;
}

type WebsitePageEventProps = {
  websiteId: string;
  pageType: PageType;
  pageName: string;
};

type WebsiteBlogPostEventProps = {
  websiteId: string;
  blogPostId: string;
};

type ReviewRequestNotificationEventProps = {
  websiteId: string;
  customerRecordId: string;
  reviewRequestId: string;
  isReminder: boolean;
};

export const DEVICE = {
  WEB: "web",
  MOBILE: "mobile",
} as const;

type DeviceType = (typeof DEVICE)[keyof typeof DEVICE];

type ProjectEventProps = {
  websiteId: string;
  deviceType: DeviceType;
};

type ReviewReplyEventProps = {
  websiteId: string;
  reviewId: string;
};

export type CrmAssetType = "lead" | "quote" | "job" | "invoice" | "appointment";

type FilterApplyEventProps = {
  websiteId: string;
  filter_type: string;
  filter_value: string;
};

type CreateFlowEventProps = {
  websiteId: string;
  source: string;
};

type CreateStepCompleteEventProps = {
  websiteId: string;
  step_name: string;
};

type DepositToggleEventProps = {
  websiteId: string;
  deposit_enabled: boolean;
};

type PaymentMethodSelectEventProps = {
  websiteId: string;
  payment_method_type: string;
  enabled: boolean;
};

type InviteEventProps = {
  organizationId: string;
  invite_source: string;
  role_assigned: string;
};

type QboLifecycleEventProps = {
  websiteId: string;
  realmId: string;
  companyName: string | null;
};

type QboDisconnectEventProps = {
  websiteId: string;
  connectionId: string;
};

// Integrations (calendar connections — Google Calendar, ICS feeds).
// `provider` is the Prisma ConnectionProvider so the same events cover
// future non-calendar connections without a schema change here.
type IntegrationEventProps = {
  websiteId: string;
  connectionId: string;
  provider: ConnectionProvider;
};

type IntegrationSyncedEventProps = IntegrationEventProps & {
  calendarId: string;
  trigger: SyncTrigger;
  eventsProcessed: number;
};

type IntegrationErroredEventProps = IntegrationEventProps & {
  calendarId: string;
  errorCode: string;
};

type IntegrationCalendarToggleEventProps = IntegrationEventProps & {
  calendarId: string;
};

type RainforestApplicationEventProps = {
  companyName: string;
  websiteId: string;
  merchantId: string;
  merchantApplicationId: string;
  declineReasons?: string[];
};

// Time Tracking — admin Timesheets dashboard

type TimesheetsAdminCsvExportEventProps = {
  websiteId: string;
  // Local calendar dates in the business timezone (`YYYY-MM-DD`),
  // matching `WeeklyGrid.weekStart` / `weekEnd`. Stored as strings (not
  // `Date`) so the analytics payload is timezone-agnostic and stable.
  weekStart: string;
  weekEnd: string;
};

type TimesheetsAdminEntryEditEventProps = {
  websiteId: string;
  // Team member whose TimeEntry was edited by the admin.
  teamMemberId: string;
  timeEntryId: string;
  // True when the corresponding field was actually changed by this
  // edit — lets analytics filter on "edits that touched the start
  // time" without having to reason about ISO equality client-side.
  startedAtChanged: boolean;
  endedAtChanged: boolean;
  notesChanged: boolean;
  // Resulting state, ISO-8601 (UTC). `null` for fields the edit did
  // not address (notesAdded === null when no note was provided, etc.).
  newStartedAt: string | null;
  newEndedAt: string | null;
  notesAdded: string | null;
};

type TimesheetsAdminEntryReviewEventProps = {
  websiteId: string;
  // Team member whose proposal was reviewed.
  teamMemberId: string;
  timeEntryId: string;
  adjustmentId: string;
  decision: "APPROVE" | "REJECT";
  // The proposed `adjustedEndAt` value the admin acted on (ISO-8601).
  // Surfaced regardless of decision so analytics can correlate
  // approved-vs-rejected proposed times.
  proposedEndedAt: string | null;
  // Note context. Three distinct fields because they're written by
  // three different actors at three different points in the
  // workflow — flattening them would lose the provenance.
  //
  // - `entryNotes`     — TimeEntry.notes at decision time. This is the
  //                      "Note" textarea content visible to the admin
  //                      in the day-detail sheet.
  // - `proposalReason` — TimeEntryAdjustment.reason — the crew
  //                      member's justification for proposing the
  //                      change.
  // - `reviewNote`     — admin's note attached to the Approve/Reject
  //                      decision. Schema accepts it but no UI surface
  //                      exposes the field today, so it'll be `null`
  //                      until that input ships.
  entryNotes: string | null;
  proposalReason: string | null;
  reviewNote: string | null;
};

// Time Tracking — crew clock-in/out + appointment timer

type TimeTrackingClockEventProps = {
  websiteId: string;
  teamMemberId: string;
  timeEntryId: string;
};

type TimeTrackingClockOutEventProps = TimeTrackingClockEventProps & {
  // Open appointment timers can be cascade-closed by clockOut. We
  // surface the appointment ids of any closed appointments here so
  // analytics can attribute the clock-out to the work that was in
  // flight at the time. Empty when nothing was cascaded.
  cascadedAppointmentIds: string[];
};

/**
 * Job/quote/client context that hangs off an appointment-bound timer
 * event. Per the BR-2315 ticket: "Metadata: any related assets's id
 * (jobs, quotes, etc.); Client information (name, phone, email)".
 *
 * Every field is nullable because Appointment has only optional
 * relations to Job, Quote, and CustomerRecord — a freeform appointment
 * with no job and no client is valid in the schema.
 */
type TimeTrackingAppointmentEventProps = {
  websiteId: string;
  teamMemberId: string;
  appointmentId: string;
  timeEntryId: string;
  jobId: string | null;
  quoteId: string | null;
  clientId: string | null;
  clientName: string | null;
  clientEmail: string | null;
  clientPhone: string | null;
};
